import { useState, useEffect } from "react";
import { Instagram, Twitter, Facebook, Send, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface SocialPrompt {
  headline: string;
  subtext: string;
  hashtag: string;
  category: 'purchase' | 'gifting';
}

const SOCIAL_PROMPTS: SocialPrompt[] = [
  // Gifting prompts
  {
    headline: "Made someone's day with a group gift! 🎁",
    subtext: "When friends come together, gifts become magical",
    hashtag: "#GroupGiftingWithShakatah #byShakatah",
    category: 'gifting'
  },
  {
    headline: "Plot twist: We all pitched in! 🎉",
    subtext: "The best gifts come from the best friends",
    hashtag: "#SplitTheGiftNotTheLove #byShakatah",
    category: 'gifting'
  },
  {
    headline: "Surprise! It was from ALL of us 💝",
    subtext: "One gift, many hearts, zero stress",
    hashtag: "#GroupGiftsAreBetterGifts #byShakatah",
    category: 'gifting'
  },
  {
    headline: "Chipped in, showed up, made memories! ✨",
    subtext: "Affordable for everyone, priceless for one",
    hashtag: "#ShakatahSquad #byShakatah",
    category: 'gifting'
  },
  // Purchase prompts
  {
    headline: "Why pay full price alone? 💸",
    subtext: "Split the cost, share the joy with friends",
    hashtag: "#SmartGroupBuying #byShakatah",
    category: 'purchase'
  },
  {
    headline: "Teamwork makes the dream work! 🛍️",
    subtext: "Group purchases = bigger savings",
    hashtag: "#BetterTogether #byShakatah",
    category: 'purchase'
  },
  {
    headline: "Splitting bills, not friendships 🤝",
    subtext: "Easy payments, happy everyone",
    hashtag: "#SplitWithShakatah #byShakatah",
    category: 'purchase'
  },
  {
    headline: "Big purchases, tiny payments! 💳",
    subtext: "Everyone pays their share, stress-free",
    hashtag: "#GroupBuyingMadeEasy #byShakatah",
    category: 'purchase'
  }
];

interface SocialShareSectionProps {
  shareUrl?: string;
  category?: 'purchase' | 'gifting' | 'all';
}

export function SocialShareSection({ 
  shareUrl = "https://www.shakatah.com/my-purchases",
  category = 'all'
}: SocialShareSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const filteredPrompts = category === 'all' 
    ? SOCIAL_PROMPTS 
    : SOCIAL_PROMPTS.filter(p => p.category === category);

  const currentPrompt = filteredPrompts[currentIndex];

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredPrompts.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, filteredPrompts.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + filteredPrompts.length) % filteredPrompts.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % filteredPrompts.length);
  };

  const shareText = `${currentPrompt.headline} ${currentPrompt.subtext} ${currentPrompt.hashtag}`;

  const handleShare = (platform: 'whatsapp' | 'twitter' | 'facebook' | 'instagram') => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, '_blank');
        break;
      case 'instagram':
        window.open('https://www.instagram.com/explore/tags/groupgiftingwithshakatah/', '_blank');
        break;
    }
  };

  return (
    <Card className="overflow-hidden border-0 bg-gradient-to-r from-customer via-purple-500 to-pink-500 shadow-lg">
      <CardContent className="p-0">
        <div className="relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-yellow-400/20 rounded-full blur-2xl" />
          </div>
          
          <div className="relative p-6 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Share the Joy!
            </div>
            
            {/* Carousel Content */}
            <div className="relative min-h-[80px] flex items-center justify-center">
              {/* Navigation Arrows */}
              <button 
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="px-8 transition-all duration-300">
                <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                  {currentPrompt.headline}
                </h3>
                <p className="text-white/80 text-sm max-w-md mx-auto">
                  {currentPrompt.subtext}
                </p>
              </div>

              <button 
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Carousel Dots */}
            <div className="flex justify-center gap-1.5 mt-3 mb-4">
              {filteredPrompts.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setIsAutoPlaying(false);
                    setCurrentIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-white w-4' : 'bg-white/40'
                  }`}
                  aria-label={`Go to prompt ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Social Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => handleShare('whatsapp')}
                className="bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                size="lg"
              >
                <Send className="w-5 h-5 mr-2" />
                WhatsApp
              </Button>
              
              <Button
                onClick={() => handleShare('instagram')}
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 hover:from-purple-600 hover:via-pink-600 hover:to-orange-500 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                size="lg"
              >
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </Button>
              
              <Button
                onClick={() => handleShare('twitter')}
                className="bg-black hover:bg-gray-800 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                size="lg"
              >
                <Twitter className="w-5 h-5 mr-2" />
                X / Twitter
              </Button>
              
              <Button
                onClick={() => handleShare('facebook')}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                size="lg"
              >
                <Facebook className="w-5 h-5 mr-2" />
                Facebook
              </Button>
            </div>

            {/* Dynamic Hashtag */}
            <p className="mt-4 text-white/60 text-xs">
              Use <span className="text-white font-medium">{currentPrompt.hashtag}</span> 💜
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}